import { assert, assertType } from '@disy/lib/custom-error';

import type { ObjecttypeWithAttributes } from 'cadenza/api-client/dto/objecttype-dto';
import type { GraphicalPropertyType } from 'cadenza/api-client/model/graphical-property-type.gen';
import { isFeatureAvailable } from 'cadenza/features';
import { AttributeCategory } from 'cadenza/repository/attribute-category';
import { AttributeType } from 'cadenza/repository/model/attribute-type.gen';
import type { BaseGeometryType } from 'cadenza/repository/model/base-geometry-type.gen';
import type { ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import { getStore } from 'cadenza/store/cadenza-store';
import { createObjecttypeMemberDetailsUrl } from 'cadenza/utils/admin-url-utils';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import type { ConditionDto } from 'cadenza/workbook/model/condition-dto';
import {
  getAllAttributesFromObjecttype,
  getAllEnrichmentsFromObjecttype,
  getAttributeById,
  getAttributes,
  getDataSourceByObjecttypeId,
  getObjecttypeById,
  getVisibleObjecttypes,
  resolveObjecttypeAttributes,
} from 'cadenza/workbook/workbook-core-selectors.state';

export function assertActiveAttributeId(
  id: ActiveAttributeId | AttributeId,
  attributeDto: WorkbookAttributeDto,
): asserts id is ActiveAttributeId {
  assertType<ActiveAttributeId>(id, attributeDto.baseId !== id);
}

export interface AttributeListController {
  accepts: (attribute: WorkbookAttributeDto) => { accepts: boolean };
}

/**
 * Filter all Objecttypes available in the workbook with the given controller.
 *
 * @param controller - The controller used for filtering
 * @return Returns a deeply filtered list of ObjectTypes. If an ObjectType does not
 *     have any attribute left it is removed.
 */
export function filterAllObjecttypes(controller: AttributeListController) {
  const visibleObjectTypes = resolveObjecttypeAttributes(getVisibleObjecttypes(getStore().getState()));
  return filterObjecttypes(visibleObjectTypes, {
    attributePredicate: (attribute) => controller.accepts(attribute).accepts,
  });
}

export function filterAllAttributesAndHierarchies(attributePredicate: (attribute: WorkbookAttributeDto) => boolean) {
  const filteredAttributes = getVisibleObjecttypes(getStore().getState()).flatMap((objecttype) => {
    const attributes = objecttype.attributes.map((attributeId) => getAttributeById(getStore().getState(), attributeId));
    return attributes.filter((a) => attributePredicate(a));
  });
  return addMissingHierarchyAttributesForLevels(filteredAttributes);
}

export function getVisibleAttributes() {
  const visibleObjecttypes = getVisibleObjecttypes(getStore().getState()).map((ot) => ot.id);
  return visibleObjecttypes.length ?
      getAttributes(getStore().getState()).filter((attr) => visibleObjecttypes.includes(attr.objecttypeId))
    : [];
}

/**
 * In many situations, if an attribute list contains a hierarchy level without its hierarchy
 * attribute, problems can occur (example: AttributeChooser, AttributeList). To ensure that any
 * level in the given list has its actual hierarchy attribute as well, use this utility.
 */
export function addMissingHierarchyAttributesForLevels(attributes: WorkbookAttributeDto[]) {
  const hierarchies = new Map<AttributeId, WorkbookAttributeDto>();

  // Find all hierarchies that are in the list already
  attributes
    .filter((attr) => attr.category === 'HIERARCHY')
    .forEach((hierarchy) => {
      hierarchies.set(hierarchy.id as AttributeId, hierarchy);
    });

  // Add missing hierarchy attributes for hierarchy levels given
  for (const attribute of attributes) {
    const hierarchyId = attribute.hierarchyId;
    if (hierarchyId && !hierarchies.has(hierarchyId)) {
      const hierarchyAttribute = getAttributeById(getStore().getState(), hierarchyId);
      hierarchies.set(hierarchyId, hierarchyAttribute);
    }
  }
  return [...attributes, ...hierarchies.values()];
}

interface FilterObjecttypeOptions {
  acceptedCategories: AttributeCategory[];
  acceptedTypes: AttributeType[];
  acceptedGeometryTypes: BaseGeometryType[];
  acceptedKeywords: string[];
  attributePredicate: (attribute: WorkbookAttributeDto) => boolean;
}

export function filterObjecttypes(
  objecttypes: ObjecttypeWithAttributes[],
  {
    acceptedCategories = [],
    acceptedTypes = [],
    acceptedGeometryTypes = [],
    acceptedKeywords = [],
    attributePredicate,
  }: Partial<FilterObjecttypeOptions> = {},
) {
  const filteredObjecttypes: ObjecttypeWithAttributes[] = [];

  // Empty means all are allowed
  acceptedCategories = acceptedCategories.length ? acceptedCategories : [...AttributeCategory.values];
  acceptedTypes = acceptedTypes.length ? acceptedTypes : [...AttributeType.values];

  objecttypes.forEach((objecttype) => {
    const printNameMatchesKeywords = acceptedKeywords.length && match(objecttype, acceptedKeywords);

    const clone: ObjecttypeWithAttributes = {
      ...objecttype,
      attributes: [],
    };

    let attributes = objecttype.attributes;
    if (objecttype.enrichments) {
      const enrichmentAttributes = objecttype.enrichments.flatMap((enrichment) =>
        getAllAttributesFromObjecttype(getStore().getState(), enrichment.objecttypeId),
      );
      attributes = [...objecttype.attributes, ...enrichmentAttributes];
    }
    attributes = attributes.filter(({ category }) => acceptedCategories.includes(category));

    if (acceptedGeometryTypes.length > 0) {
      // if we have an accepted GeometryType defined, we do not care about the type, only the
      // geometry type is relevant
      // !: relatedGeometryType is always set on geometry attributes
      attributes = attributes.filter(({ relatedGeometryType }) => acceptedGeometryTypes.includes(relatedGeometryType!));
    } else {
      // Hierarchies don't have a type, so we just keep them in list.
      attributes = attributes.filter(
        ({ category, type, hierarchyType }) => hierarchyType || acceptedTypes.includes(type),
      );
    }

    // Filter by keywords in attribute printNames
    if (!printNameMatchesKeywords && acceptedKeywords.length) {
      attributes = matchAttributes(attributes, acceptedKeywords);
    }

    // Additional validation
    if (attributePredicate) {
      attributes = attributes.filter(attributePredicate);
    }

    // Only add if there are attributes still left
    if (attributes.length) {
      // remove enrichments
      attributes = attributes.filter(({ objecttypeId }) => objecttypeId === objecttype.id);
      clone.attributes = attributes;
      filteredObjecttypes.push(clone);
    }
  });
  return filteredObjecttypes;
}

function matchAttributes(attributes: WorkbookAttributeDto[], keywords: string[]) {
  const matchingAttributes = [];
  for (let i = 0; i < attributes.length; i++) {
    const attribute = attributes[i];
    if (attribute.hierarchyType) {
      const hierarchyId = attribute.id;
      const hierarchyAttributes = [attribute];
      while (++i < attributes.length && attributes[i].hierarchyId === hierarchyId) {
        hierarchyAttributes.push(attributes[i]);
      }
      if (hierarchyAttributes.some((_) => match(_, keywords))) {
        matchingAttributes.push(...hierarchyAttributes);
      }
    } else if (match(attribute, keywords)) {
      matchingAttributes.push(attribute);
    }
  }
  return matchingAttributes;
}

function match({ printName }: { printName: string }, keywords: string[]) {
  const lowerCasePrintName = printName.toLowerCase();
  return keywords.some((keyword) => lowerCasePrintName.includes(keyword));
}

/**
 * Finds an object type related to which the condition belongs to (via the first attribute). Throws
 * an error if none is found.
 *
 * @param objecttypes - The objecttypes where to look
 * @param condition - The condition to which the object type needs to be found
 * @return object type the condition belongs to
 */
export function findObjecttype(objecttypes: ObjecttypeWithAttributes[], condition: ConditionDto) {
  const firstAttributeId = condition.attributeIds[0];
  for (const objecttype of objecttypes) {
    for (const attribute of objecttype.attributes) {
      if (attribute.id === firstAttributeId) {
        return objecttype;
      }
    }
  }
  throw new Error('Could not find objecttype containing attributeId: ' + firstAttributeId);
}

export function isLobAttributeWithoutRelation(attribute: WorkbookAttributeDto) {
  return attribute.type === 'LOB' && !attribute.hasLobRelation;
}

export function getGroupedEnrichmentsFromObjecttype(objecttypeId: ObjecttypeIdString) {
  const enrichments = getAllEnrichmentsFromObjecttype(getStore().getState(), objecttypeId);
  const result = [];
  for (const enrichment of enrichments) {
    if (enrichment.grouped) {
      result.push(enrichment);
    }
  }
  return result;
}

export function getGroupedEnrichmentObjecttypeIdsFromObjecttype(objecttypeId: ObjecttypeIdString) {
  return getGroupedEnrichmentsFromObjecttype(objecttypeId).map((enrichment) => enrichment.objecttypeId);
}

export function createEditAttributeColoringUrl(attribute: WorkbookAttributeDto) {
  // BOT id
  const objecttypeId = attribute.underlyingObjectTypeId ?? attribute.objecttypeId;
  const objecttype = getObjecttypeById(getStore().getState(), attribute.objecttypeId);

  const isUnboundObjecttype = objecttype.boundState === 'UNBOUND';
  const isAdministrationAvailable =
    isFeatureAvailable('ADMINISTRATION') &&
    getObjecttypeById(getStore().getState(), objecttype.id).currentUserPrivileges.includes('visible');

  if (isUnboundObjecttype && isAdministrationAvailable) {
    // when the attribute is part of a hierarchy, the name is no longer matching the member name
    return (
      createObjecttypeMemberDetailsUrl(
        objecttypeId,
        attribute.hierarchyId ? attribute.objecttypeAttributeName! : attribute.name,
      ) + '?tab=coloring'
    );
  }
}

export function workbookAttributeHasNameRelation(attribute: WorkbookAttributeDto) {
  const attrId = attribute.baseId;
  return getObjecttypeById(getStore().getState(), attribute.objecttypeId).attributeRelationGroups?.some(
    (group) =>
      group.nameAttribute === attrId ||
      (group.nameAttribute &&
        [...group.idAttributes, ...group.lobAttributes, group.geometryAttribute].includes(attrId)),
  );
}

export function assertStandardModeStyleRulesForType(type: GraphicalPropertyType, attribute: WorkbookAttributeDto) {
  attribute.attributeStyleRules?.forEach((attributeStyleRule) => {
    const styleRules = attributeStyleRule.styleRules;
    assert(styleRules.length === 1, 'in standard mode there is exactly one style rule per class');
    const styleRule = styleRules[0];
    assert(styleRule.type === type, 'in standard mode the style rule must be of the requested type');
  });
  return attribute.attributeStyleRules ?? [];
}

export function getAllowedAggregations(attribute: WorkbookAttributeDto) {
  const dataSource = getDataSourceByObjecttypeId(getStore().getState(), attribute.objecttypeId);
  return dataSource.measureAggregationsByAttributeType[attribute.type];
}
